import { useState, useEffect } from 'react';
import API from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { HelpCircle, Phone, Mail, MessageCircle, Clock, Plus, Send, ExternalLink, FileText, ChevronRight, Loader2, Building2, Search, FlaskConical, Pill, Package } from 'lucide-react';
import { toast } from 'sonner';

export default function SupportPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactInfo, setContactInfo] = useState({});
  const [vendors, setVendors] = useState([]);
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendorTypeFilter, setVendorTypeFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [viewTicket, setViewTicket] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [newTicket, setNewTicket] = useState({
    subject: '',
    message: '',
    category: 'general',
    priority: 'normal'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ticketsRes, contactRes, vendorsRes] = await Promise.all([
        API.get('/support/tickets'),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/public/support-contacts/doctor`).then(r => r.ok ? r.json() : null).catch(() => null),
        API.get('/b2b/vendors/approved').catch(() => ({ data: [] }))
      ]);
      setTickets(ticketsRes.data);
      if (contactRes) {
        setContactInfo(contactRes);
      }
      setVendors(vendorsRes.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredVendors = vendors.filter(v => {
    const matchesSearch = !vendorSearch || 
      v.name?.toLowerCase().includes(vendorSearch.toLowerCase()) ||
      v.email?.toLowerCase().includes(vendorSearch.toLowerCase());
    const matchesType = vendorTypeFilter === 'all' || v.vendor_type === vendorTypeFilter;
    return matchesSearch && matchesType;
  });

  const getVendorIcon = (type) => {
    switch(type) {
      case 'lab': return <FlaskConical className="w-4 h-4 text-blue-600" />;
      case 'pharma': return <Pill className="w-4 h-4 text-green-600" />;
      case 'inventory': return <Package className="w-4 h-4 text-amber-600" />;
      default: return <Building2 className="w-4 h-4 text-slate-600" />;
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.subject.trim() || !newTicket.message.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await API.post('/support/tickets', newTicket);
      toast.success('Support ticket created successfully');
      setCreateOpen(false);
      setNewTicket({ subject: '', message: '', category: 'general', priority: 'normal' });
      fetchData();
    } catch (err) {
      toast.error('Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim()) return;
    setSubmitting(true);
    try {
      const res = await API.post(`/support/tickets/${viewTicket.id}/reply`, { message: replyText });
      setViewTicket(res.data);
      setReplyText('');
      fetchData();
      toast.success('Reply sent');
    } catch (err) {
      toast.error('Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-700';
      case 'in_progress': return 'bg-yellow-100 text-yellow-700';
      case 'resolved': return 'bg-green-100 text-green-700';
      case 'closed': return 'bg-slate-100 text-slate-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-700';
      case 'high': return 'bg-orange-100 text-orange-700';
      case 'normal': return 'bg-slate-100 text-slate-700';
      case 'low': return 'bg-slate-50 text-slate-500';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="support-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Help & Support</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Get help with the platform, marketplace, or billing</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-teal-700 hover:bg-teal-800" data-testid="create-ticket-btn">
              <Plus className="w-4 h-4 mr-2" /> New Ticket
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope' }}>Create Support Ticket</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTicket} className="space-y-4">
              <div>
                <Label>Subject *</Label>
                <Input
                  data-testid="ticket-subject"
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                  placeholder="Brief description of your issue"
                  className="mt-1"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select value={newTicket.category} onValueChange={(v) => setNewTicket({ ...newTicket, category: v })}>
                    <SelectTrigger className="mt-1" data-testid="ticket-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General Inquiry</SelectItem>
                      <SelectItem value="technical">Technical Issue</SelectItem>
                      <SelectItem value="billing">Billing</SelectItem>
                      <SelectItem value="marketplace">Marketplace</SelectItem>
                      <SelectItem value="orders">Orders</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={newTicket.priority} onValueChange={(v) => setNewTicket({ ...newTicket, priority: v })}>
                    <SelectTrigger className="mt-1" data-testid="ticket-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Message *</Label>
                <Textarea
                  data-testid="ticket-message"
                  value={newTicket.message}
                  onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                  placeholder="Describe your issue in detail..."
                  className="mt-1"
                  rows={4}
                  required
                />
              </div>
              <Button type="submit" className="w-full bg-teal-700 hover:bg-teal-800" disabled={submitting} data-testid="submit-ticket-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {submitting ? 'Submitting...' : 'Submit Ticket'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Quick Contact Info - Super Admin */}
      <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2 text-purple-700">
            <Building2 className="w-5 h-5" /> 1Clin Platform Support
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
              <Phone className="w-5 h-5 text-purple-600" />
              <div>
                <p className="text-xs text-slate-500">Phone</p>
                <p className="font-medium">{contactInfo?.support_phone || 'Not configured'}</p>
              </div>
              {contactInfo?.support_phone && (
                <Button size="sm" variant="outline" className="ml-auto" onClick={() => window.open(`tel:${contactInfo.support_phone}`)}>Call</Button>
              )}
            </div>
            <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
              <Mail className="w-5 h-5 text-purple-600" />
              <div>
                <p className="text-xs text-slate-500">Email</p>
                <p className="font-medium">{contactInfo?.support_email || 'Not configured'}</p>
              </div>
              {contactInfo?.support_email && (
                <Button size="sm" variant="outline" className="ml-auto" onClick={() => window.open(`mailto:${contactInfo.support_email}`)}>Email</Button>
              )}
            </div>
            <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
              <MessageCircle className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-xs text-slate-500">WhatsApp</p>
                <p className="font-medium">{contactInfo?.whatsapp_number || 'Not configured'}</p>
              </div>
              {contactInfo?.whatsapp_number && (
                <Button size="sm" variant="outline" className="ml-auto" onClick={() => window.open(`https://wa.me/${contactInfo.whatsapp_number.replace(/\D/g, '')}`)}>Chat</Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for Tickets and Vendor Directory */}
      <Tabs defaultValue="tickets" className="space-y-4">
        <TabsList className="w-full">
          <TabsTrigger value="tickets" className="flex-1 text-xs sm:text-sm">My Tickets</TabsTrigger>
          <TabsTrigger value="vendors" className="flex-1 text-xs sm:text-sm">Vendor Directory</TabsTrigger>
        </TabsList>

        {/* Support Tickets Tab */}
        <TabsContent value="tickets">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <FileText className="w-5 h-5 text-teal-600" /> My Support Tickets
              </CardTitle>
              <CardDescription>Track your support requests and conversations</CardDescription>
            </CardHeader>
            <CardContent>
              {tickets.length === 0 ? (
                <div className="text-center py-8">
                  <HelpCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No support tickets yet</p>
                  <p className="text-sm text-slate-400 mt-1">Create a ticket if you need help</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {tickets.map((ticket) => (
                    <div
                      key={ticket.id}
                      className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                      onClick={() => setViewTicket(ticket)}
                      data-testid={`ticket-${ticket.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs text-slate-400 font-mono">{ticket.ticket_number}</span>
                            <Badge className={`text-xs ${getStatusColor(ticket.status)}`}>{ticket.status.replace('_', ' ')}</Badge>
                            <Badge className={`text-xs ${getPriorityColor(ticket.priority)}`}>{ticket.priority}</Badge>
                          </div>
                          <p className="font-medium text-slate-900">{ticket.subject}</p>
                          <p className="text-sm text-slate-500 mt-1 line-clamp-1">{ticket.message}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400">{new Date(ticket.created_at).toLocaleDateString()}</span>
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vendor Directory Tab */}
        <TabsContent value="vendors">
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                    <Building2 className="w-5 h-5 text-teal-600" /> Vendor Directory
                  </CardTitle>
                  <CardDescription>Contact information for all approved vendors</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search vendors..."
                      value={vendorSearch}
                      onChange={(e) => setVendorSearch(e.target.value)}
                      className="pl-10 w-64"
                      data-testid="vendor-search"
                    />
                  </div>
                  <Select value={vendorTypeFilter} onValueChange={setVendorTypeFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="lab">Labs</SelectItem>
                      <SelectItem value="pharma">Pharma</SelectItem>
                      <SelectItem value="chemist">Chemist</SelectItem>
                      <SelectItem value="inventory">Inventory</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredVendors.length === 0 ? (
                <div className="text-center py-8">
                  <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No vendors found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredVendors.map((vendor) => (
                    <div key={vendor.id} className="p-4 bg-slate-50 rounded-lg" data-testid={`vendor-${vendor.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          {vendor.logo_url ? (
                            <img src={vendor.logo_url.startsWith('http') ? vendor.logo_url : `${process.env.REACT_APP_BACKEND_URL}${vendor.logo_url}`} alt={vendor.name} className="w-10 h-10 rounded-lg object-cover border" />
                          ) : (
                            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border">
                              {getVendorIcon(vendor.vendor_type)}
                            </div>
                          )}
                          <div>
                            <h4 className="font-medium text-slate-900">{vendor.name}</h4>
                            <Badge variant="outline" className="capitalize mt-1">{vendor.vendor_type}</Badge>
                            {vendor.address && <p className="text-sm text-slate-500 mt-2">{vendor.address}</p>}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 items-end">
                          {vendor.phone && (
                            <Button size="sm" variant="outline" className="h-8" onClick={() => window.open(`tel:${vendor.phone}`)}>
                              <Phone className="w-3 h-3 mr-1" /> {vendor.phone}
                            </Button>
                          )}
                          {vendor.email && (
                            <Button size="sm" variant="outline" className="h-8" onClick={() => window.open(`mailto:${vendor.email}`)}>
                              <Mail className="w-3 h-3 mr-1" /> Email
                            </Button>
                          )}
                          {vendor.phone && (
                            <Button size="sm" variant="outline" className="h-8 text-green-600 border-green-200 hover:bg-green-50" onClick={() => window.open(`https://wa.me/${vendor.phone.replace(/\D/g, '')}`)}>
                              <MessageCircle className="w-3 h-3 mr-1" /> WhatsApp
                            </Button>
                          )}
                        </div>
                      </div>
                      {/* MR Executive Info if available */}
                      {vendor.mr_executive?.name && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <p className="text-xs text-slate-500 mb-1">MR/Sales Executive:</p>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="font-medium">{vendor.mr_executive.name}</span>
                            {vendor.mr_executive.phone && (
                              <span className="text-slate-500">{vendor.mr_executive.phone}</span>
                            )}
                            {vendor.mr_executive.territory && (
                              <Badge variant="secondary" className="text-xs">{vendor.mr_executive.territory}</Badge>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Support Hours Card */}
      <Card className="border-slate-200">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-slate-500" />
            <span className="text-sm text-slate-600">
              <strong>Support Hours:</strong> {contactInfo?.business_hours || 'Mon-Fri 9AM-6PM IST'}
            </span>
          </div>
          {contactInfo?.office_address && (
            <div className="flex items-center gap-3 mt-2">
              <Building2 className="w-5 h-5 text-slate-500" />
              <span className="text-sm text-slate-600">
                <strong>Office:</strong> {contactInfo.office_address}
              </span>
            </div>
          )}
          {contactInfo?.additional_info && (
            <div className="mt-2 text-sm text-slate-500 pl-8">{contactInfo.additional_info}</div>
          )}
        </CardContent>
      </Card>

      {/* View Ticket Dialog */}
      <Dialog open={!!viewTicket} onOpenChange={() => setViewTicket(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {viewTicket && (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope' }}>
                  <span className="text-xs text-slate-400 font-mono block mb-1">{viewTicket.ticket_number}</span>
                  {viewTicket.subject}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Badge className={getStatusColor(viewTicket.status)}>{viewTicket.status.replace('_', ' ')}</Badge>
                  <Badge className={getPriorityColor(viewTicket.priority)}>{viewTicket.priority}</Badge>
                  <Badge variant="outline">{viewTicket.category}</Badge>
                </div>
                
                {/* Messages Thread */}
                <div className="space-y-3 border rounded-lg p-4 bg-slate-50 max-h-[300px] overflow-y-auto">
                  {/* Original Message */}
                  <div className="bg-white p-3 rounded-lg border">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-medium text-slate-900">You</span>
                      <span className="text-xs text-slate-400">{new Date(viewTicket.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{viewTicket.message}</p>
                  </div>
                  
                  {/* Responses */}
                  {viewTicket.responses?.map((response) => (
                    <div key={response.id} className={`p-3 rounded-lg border ${response.is_admin ? 'bg-teal-50 border-teal-200' : 'bg-white'}`}>
                      <div className="flex justify-between items-start mb-2">
                        <span className={`text-sm font-medium ${response.is_admin ? 'text-teal-700' : 'text-slate-900'}`}>
                          {response.is_admin ? '1Clin Support' : 'You'}
                        </span>
                        <span className="text-xs text-slate-400">{new Date(response.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-sm text-slate-600 whitespace-pre-wrap">{response.message}</p>
                    </div>
                  ))}
                </div>

                {/* Reply Box */}
                {viewTicket.status !== 'closed' && (
                  <div className="flex gap-2">
                    <Textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type your reply..."
                      rows={2}
                      className="flex-1"
                      data-testid="ticket-reply-input"
                    />
                    <Button onClick={handleReply} disabled={submitting || !replyText.trim()} data-testid="send-reply-btn">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
